/* ============================================================
   PHREE SKIN — /quiz logic (vanilla, no deps beyond GSAP for polish).
   FULL-SCREEN immersive: one question fills the viewport at a time.
   7-dot Braille progress · single-choice auto-advance · number-key
   shortcuts (1-4) · email capture · glass result reveal.
   Bulgarian is the inline default; English lives in data-en (i18n.js).
   ============================================================ */
(function () {
  "use strict";

  /* lock html so nothing scrolls behind the fixed shell */
  document.documentElement.classList.add("quiz-html");

  /* the REAL 7-dot Braille mark (mirrors app.js MARK; app.js isn't loaded here) */
  var MARK = window.PHREE_MARK ||
    '<svg viewBox="160 176 322 235" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
    '<circle cx="191.2" cy="207" r="30.5"/><circle cx="191.2" cy="293.7" r="30.5"/><circle cx="191.2" cy="380.3" r="30.5"/>' +
    '<circle cx="279.1" cy="207" r="30.5"/><circle cx="363.9" cy="293.7" r="30.5"/><circle cx="363.9" cy="380.3" r="30.5"/>' +
    '<circle cx="451.8" cy="207" r="30.5"/></svg>';
  document.querySelectorAll("[data-logo]").forEach(function (el) { el.innerHTML = MARK; });

  var root     = document.getElementById("quiz");
  var stage    = document.getElementById("quizStage");
  var steps    = Array.prototype.slice.call(stage.querySelectorAll(".qstep"));
  var progress = document.getElementById("quizProgress");
  var dots     = Array.prototype.slice.call(progress.querySelectorAll(".qdot"));
  var barFill  = document.getElementById("quizBarFill");
  var stepNum  = document.getElementById("qStep");
  var totalEl  = document.getElementById("qTotal");
  var backBtn  = document.getElementById("quizBack");
  var form     = document.getElementById("qForm");
  var emailIn  = document.getElementById("qEmail");
  var nameIn   = document.getElementById("qName");
  var emailField = emailIn.closest(".qfield");
  var resultEl = document.getElementById("quizResult");
  var whyEl    = document.getElementById("qresultWhy");
  var sentEl   = document.getElementById("qresultSent");
  var restart  = document.getElementById("quizRestart");

  if (!stage || !steps.length) return;

  var gsap  = window.gsap || null;
  var TOTAL = steps.length;         // 4
  var DOTS  = dots.length;          // 7 (Braille motif)
  var current = 0;                  // 0-indexed active step
  var answers = { skin: null, concern: null, routine: null, email: "", name: "" };
  var LOCK = false;                 // guard against double-advance during transition

  totalEl && (totalEl.textContent = TOTAL);

  var reduce = matchMedia("(prefers-reduced-motion: reduce)").matches;
  var STEP_WAIT = reduce ? 0 : 300;   // ms — let the pick register before advancing

  /* ---------- Braille progress + top bar-fill ---------- */
  function paintProgress(activeIndex, done) {
    var lit = done ? DOTS : Math.round((activeIndex / TOTAL) * DOTS);
    var now = done ? -1 : Math.min(DOTS - 1, lit);
    dots.forEach(function (d, i) {
      d.classList.toggle("is-on", done || i < lit);
      d.classList.toggle("is-now", i === now);
    });
    if (barFill) {
      // width reflects how far into the flow you are: step 1 shows 1/TOTAL, last shows TOTAL/TOTAL
      var pct = done ? 100 : Math.min(100, Math.round(((activeIndex + 1) / TOTAL) * 100));
      barFill.style.width = pct + "%";
    }
    if (progress) progress.setAttribute("aria-valuenow", String(Math.min(TOTAL, activeIndex + 1)));
  }

  function setCount(index) { stepNum && (stepNum.textContent = Math.min(TOTAL, index + 1)); }

  /* ---------- show a step ---------- */
  function show(index, dir) {
    if (index < 0 || index >= TOTAL) return;
    var from = steps[current];
    var to   = steps[index];

    if (from && from !== to) {
      from.classList.toggle("is-leave", (dir || 1) > 0);   // upward on forward, downward on back
      from.classList.remove("is-active");
      window.setTimeout(function () { from.classList.remove("is-leave"); }, 620);
    }
    to.classList.remove("is-leave");
    to.classList.remove("is-active");
    void to.offsetWidth;                 // reflow so the entrance re-plays
    to.classList.add("is-active");
    current = index;

    setCount(index);
    paintProgress(index, false);
    backBtn.hidden = index === 0;

    // focus management (accessible, keyboard-friendly) — but not on the email step's inputs,
    // where auto-focus would pop the mobile keyboard and cover the screen. Focus the step instead.
    if (!reduce) {
      var isForm = !!to.querySelector("form");
      var focusTarget = isForm ? null : to.querySelector(".qopt");
      if (focusTarget) focusTarget.focus({ preventScroll: true });
    }
    // reset stage scroll so long steps start at the top
    to.scrollTop = 0;
    syncI18n();
  }

  /* ---------- pick an option (single-choice, auto-advance) ---------- */
  function pick(btn) {
    if (LOCK || !btn) return;
    var q = btn.dataset.q, val = btn.dataset.val;
    var group = btn.closest(".qopts");
    answers[q] = val;

    group.querySelectorAll(".qopt").forEach(function (o) {
      var on = o === btn;
      o.classList.toggle("is-picked", on);
      o.setAttribute("aria-checked", on ? "true" : "false");
    });

    LOCK = true;
    window.setTimeout(function () {
      LOCK = false;
      if (current < TOTAL - 1) show(current + 1, 1);
    }, STEP_WAIT);
  }

  stage.addEventListener("click", function (e) {
    var btn = e.target.closest(".qopt");
    if (btn) pick(btn);
  });

  /* ---------- keyboard: 1-4 shortcuts + arrows + enter/space on options ---------- */
  document.addEventListener("keydown", function (e) {
    if (root.classList.contains("is-done")) return;
    var active = steps[current];
    var tag = (e.target.tagName || "").toLowerCase();
    var typing = tag === "input" || tag === "textarea";

    // number-key select on question steps
    if (!typing && /^[1-9]$/.test(e.key)) {
      var opts = active.querySelectorAll(".qopt");
      var idx = parseInt(e.key, 10) - 1;
      if (opts[idx]) { e.preventDefault(); pick(opts[idx]); }
      return;
    }
    // enter/space activates a focused option
    var onOpt = e.target.closest && e.target.closest(".qopt");
    if (onOpt && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); pick(onOpt); return; }
    // back navigation
    if (!typing && (e.key === "ArrowUp" || (e.key === "Backspace" && current > 0))) {
      if (current > 0) { e.preventDefault(); show(current - 1, -1); }
    }
  });

  /* ---------- back ---------- */
  backBtn.addEventListener("click", function () { if (current > 0) show(current - 1, -1); });

  /* ---------- email validation ---------- */
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  function validEmail(v) { return EMAIL_RE.test((v || "").trim()); }
  emailIn.addEventListener("input", function () {
    if (emailField.classList.contains("is-invalid") && validEmail(emailIn.value)) emailField.classList.remove("is-invalid");
  });

  /* ---------- rationale, keyed to the answers ---------- */
  var WHY = {
    dryness:    { bg: "Кожата ти губи влага бързо — този серум изгражда липидната бариера за трайна хидратация, която издържа.",
                  en: "Your skin loses moisture fast — this serum rebuilds the lipid barrier for lasting hydration that actually holds." },
    redness:    { bg: "За зачервяване залагаме на микробиома: пре- и пробиотичните активи успокояват реактивните микроби и намаляват видимото зачервяване.",
                  en: "For redness we work with the microbiome: pre- and probiotic actives calm reactive microbes and visibly ease flushing." },
    barrier:    { bg: "Нарушена бариера значи щипене и грубост — ниацинамид и плътна липидна мантия я възстановяват стъпка по стъпка.",
                  en: "A weakened barrier means stinging and roughness — niacinamide and a dense lipid mantle rebuild it, step by step." },
    reactivity: { bg: "Реактивна кожа има нужда от баланс, не от още активи — един серум, който успокоява и изгражда трайна устойчивост.",
                  en: "Reactive skin needs balance, not more actives — one serum that calms and builds lasting resilience." },
    _default:   { bg: "Бариера + микробиом в една стъпка — създаден точно за кожата, която описа.",
                  en: "Barrier + microbiome in one step — built for exactly the skin you described." }
  };
  function setWhy() {
    var w = WHY[answers.concern] || WHY._default;
    /* Pick the language from the document, the same way assets/theme-ui.js already does.
       This previously rendered w.bg unconditionally and left the English on a data-en attribute
       for window.I18N to swap — but assets/i18n.js is never loaded by layout/theme.liquid, so
       window.I18N never existed, syncI18n() was a permanent no-op, and every English blurb below
       was dead code. Reading documentElement.lang is the pattern that demonstrably works here. */
    var en = document.documentElement.lang === "en";
    whyEl.textContent = en ? w.en : w.bg;
    whyEl.setAttribute("data-en", w.en);
    whyEl.__bg = w.bg;
  }

  /* ---------- Shopify-captcha-safe /contact POST (hidden iframe, no fetch) ----------
     Shopify auto-wires hCaptcha bot-protection onto any form whose action contains
     "/contact" and carries a form_type=customer|contact hidden field (exactly what the
     {% form 'customer' %} Liquid tag emits). A synthetic FormData + fetch() never gets a
     captcha token attached and is rejected with "Missing CAPTCHA token". The documented
     fix is a real <form> element run through window.Shopify.captcha.protect(form, cb),
     submitted via a hidden iframe target so the page never navigates away. */
  function submitContactForm(fields) {
    var IFRAME_NAME = "ph-contact-frame";
    var iframe = document.getElementsByName(IFRAME_NAME)[0];
    if (!iframe) {
      iframe = document.createElement("iframe");
      iframe.name = IFRAME_NAME;
      iframe.hidden = true;
      iframe.style.display = "none";
      document.body.appendChild(iframe);
    }
    var f = document.createElement("form");
    f.action = "/contact";
    f.method = "post";
    f.acceptCharset = "UTF-8";
    f.target = IFRAME_NAME;
    f.hidden = true;
    f.style.display = "none";
    Object.keys(fields).forEach(function (name) {
      var input = document.createElement("input");
      input.type = "hidden";
      input.name = name;
      input.value = fields[name];
      f.appendChild(input);
    });
    document.body.appendChild(f);

    function doSubmit() {
      try { f.submit(); } catch (e) {}
      window.setTimeout(function () { f.parentNode && f.parentNode.removeChild(f); }, 1000);
    }

    try {
      if (window.Shopify && window.Shopify.captcha && typeof window.Shopify.captcha.protect === "function") {
        window.Shopify.captcha.protect(f, doSubmit);
      } else {
        doSubmit();
      }
    } catch (e) { doSubmit(); }
  }

  /* ---------- finish → reveal result ---------- */
  function finish() {
    setWhy();
    answers.email = emailIn.value.trim();
    answers.name  = (nameIn.value || "").trim();
    try { localStorage.setItem("phree-quiz", JSON.stringify(answers)); } catch (e) {}

    /* Subscribe + tag the shopper in Shopify — native customer form, no app, no backend.
       The result rides along as tags (quiz, skin:*, concern:*, routine:*) so the owner's
       email automation (Shopify Email or Klaviyo) can greet them and segment later. */
    try {
      var tags = ["quiz"];
      if (answers.skin) tags.push("skin:" + answers.skin);
      if (answers.concern) tags.push("concern:" + answers.concern);
      if (answers.routine) tags.push("routine:" + answers.routine);
      var fields = {
        form_type: "customer",
        utf8: "✓",
        "contact[email]": answers.email,
        "contact[tags]": tags.join(",")
      };
      if (answers.name) fields["contact[first_name]"] = answers.name;
      submitContactForm(fields);
    } catch (e) {}

    // blur so the mobile keyboard drops before the reveal
    if (document.activeElement && document.activeElement.blur) document.activeElement.blur();

    paintProgress(TOTAL, true);
    setCount(TOTAL - 1);

    if (sentEl) {
      var greetBg = answers.name ? ("Готово, " + answers.name + " — ето препоръката за твоята кожа.") : "Ето препоръката за твоята кожа.";
      var greetEn = answers.name ? ("Done, " + answers.name + " — here's the match for your skin.") : "Here's the match for your skin.";
      /* same fix as setWhy(): pick from documentElement.lang rather than relying on the
         never-loaded window.I18N to swap data-en in afterwards */
      sentEl.textContent = document.documentElement.lang === "en" ? greetEn : greetBg;
      sentEl.setAttribute("data-en", greetEn); sentEl.__bg = greetBg;
      sentEl.hidden = false;
    }

    root.classList.add("is-done");
    resultEl.hidden = false;

    var card = resultEl.querySelector(".qresult__card");
    if (card) {
      if (reduce || !gsap) { card.style.opacity = 1; card.style.transform = "none"; }
      else { gsap.fromTo(card, { opacity: 0, y: 26 }, { opacity: 1, y: 0, duration: 0.6, ease: "power3.out", delay: 0.08 }); }
    }
    syncI18n();
  }

  form && form.addEventListener("submit", function (e) {
    e.preventDefault();
    if (!validEmail(emailIn.value)) { emailField.classList.add("is-invalid"); emailIn.focus(); return; }
    emailField.classList.remove("is-invalid");
    finish();
  });

  /* ---------- restart ---------- */
  restart && restart.addEventListener("click", function () {
    answers = { skin: null, concern: null, routine: null, email: "", name: "" };
    stage.querySelectorAll(".qopt").forEach(function (o) {
      o.classList.remove("is-picked"); o.setAttribute("aria-checked", "false");
    });
    if (form) form.reset();
    emailField.classList.remove("is-invalid");
    sentEl && (sentEl.hidden = true);
    resultEl.hidden = true;
    root.classList.remove("is-done");
    steps.forEach(function (s) { s.classList.remove("is-active", "is-leave"); });
    current = 0;
    steps[0].classList.add("is-active");
    paintProgress(0, false);
    setCount(0);
    backBtn.hidden = true;
  });

  /* ---------- i18n glue ---------- */
  /* assets/i18n.js is not loaded by layout/theme.liquid, so window.I18N never exists. Kept as a
     no-op shim so any remaining caller stays harmless; language now comes from documentElement.lang. */
  function syncI18n() { if (window.I18N && window.I18N.apply) window.I18N.apply(); }
  document.addEventListener("langchange", syncI18n);

  /* ---------- boot ---------- */
  steps.forEach(function (s) {
    Array.prototype.slice.call(s.querySelectorAll(".qopt")).forEach(function (o, i) { o.style.setProperty("--stagI", i); });
  });
  steps.forEach(function (s, i) { s.classList.toggle("is-active", i === 0); s.classList.remove("is-leave"); });
  current = 0;
  paintProgress(0, false);
  setCount(0);
  backBtn.hidden = true;
})();
